import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { tr } from "./i18n.js";

const ADMINISTRARE = ["ADMIN_TENANT", "MANAGER_TEHNIC"];
const POATE_PROPUNE = [...ADMINISTRARE, "TEHNICIAN", "COORDONATOR"];

const ETICHETA_CATEGORIE = {
  tip_componenta: "tip de componentă",
  articol: "articol de catalog",
  operatie: "operație tehnică",
};

export default function Nomenclator({ eu }) {
  const [arbore, setArbore] = useState([]);
  const [sistemActiv, setSistemActiv] = useState(null);
  const [inchise, setInchise] = useState({});
  const [aratăDezactivate, setAratăDezactivate] = useState(false);
  const [incarca, setIncarca] = useState(true);
  const [eroare, setEroare] = useState("");

  const [interogare, setInterogare] = useState("");
  const [rezultate, setRezultate] = useState(null);
  const [cautaAcum, setCautaAcum] = useState(false);

  const [propuneri, setPropuneri] = useState([]);
  const [formular, setFormular] = useState(null);

  const poateAdministra = ADMINISTRARE.includes(eu.rol);
  const poatePropune = POATE_PROPUNE.includes(eu.rol);

  const reincarca = useCallback(async () => {
    try {
      const date = await api.arbore(aratăDezactivate);
      setArbore(date);
      setSistemActiv((curent) => curent || date[0]?.id || null);
      if (poateAdministra) setPropuneri(await api.propuneri());
    } catch (e) {
      setEroare(mesajEroare(e));
    } finally {
      setIncarca(false);
    }
  }, [poateAdministra, aratăDezactivate]);

  useEffect(() => { reincarca(); }, [reincarca]);

  const ultimaCerere = useRef(0);
  useEffect(() => {
    const q = interogare.trim();
    if (q.length < 2) { setRezultate(null); setCautaAcum(false); return; }
    setCautaAcum(true);
    const cerere = ++ultimaCerere.current;
    const cronometru = setTimeout(async () => {
      try {
        const r = await api.cauta(q);
        if (cerere === ultimaCerere.current) setRezultate(r);
      } catch (e) {
        if (cerere === ultimaCerere.current) setEroare(mesajEroare(e));
      } finally {
        if (cerere === ultimaCerere.current) setCautaAcum(false);
      }
    }, 250);
    return () => clearTimeout(cronometru);
  }, [interogare]);

  const sistem = useMemo(
    () => arbore.find((s) => s.id === sistemActiv) || arbore[0] || null,
    [arbore, sistemActiv],
  );

  async function actiune(promisiune) {
    setEroare("");
    try { await promisiune; await reincarca(); }
    catch (e) { setEroare(mesajEroare(e)); }
  }

  if (incarca) return <div className="continut gol">{tr("Se încarcă nomenclatorul…")}</div>;

  const totalTipuri = arbore.reduce(
    (n, s) => n + s.subsisteme.reduce((m, ss) => m + ss.tipuriComponenta.length, 0), 0,
  );

  return (
    <div className="continut">
      <h2>{tr("Nomenclator")}</h2>
      <p className="subtitlu">
        {tr("Ce fel de echipamente există, pe sisteme. Pe ele se sprijină parcul instalat, operațiile și fișele de intervenție.")} {arbore.length} {tr("sisteme,")} {totalTipuri} {tr("tipuri.")}
      </p>

      {eroare && <div className="eroare">{eroare}</div>}

      <input
        className="camp camp-cautare"
        placeholder={tr("Caută în tot nomenclatorul — „camera dome”, „maglock”, „sirena”…")}
        value={interogare}
        onChange={(e) => setInterogare(e.target.value)}
      />

      {rezultate !== null ? (
        <div className="panou">
          <div className="panou-antet">
            <span>{cautaAcum ? tr("Se caută…") : tr("{0} rezultate pentru „{1}”", rezultate.length, interogare.trim())}</span>
            <button className="buton buton-mic" onClick={() => setInterogare("")}>{tr("înapoi la sisteme")}</button>
          </div>
          <div className="panou-corp">
            {rezultate.length === 0 ? (
              <div className="gol">
                {tr("Nimic găsit. Dacă echipamentul există la tine dar nu e în listă, deschide sistemul din care face parte și adaugă-l acolo — rămâne înregistrat în nomenclator, disponibil de-acum înainte.")}
              </div>
            ) : (
              <ul className="lista-rezultate">
                {rezultate.map((r) => (
                  <li key={r.categorie + r.id}>
                    <span className={"eticheta " + (r.exact ? "eticheta-activ" : "")}>
                      {r.exact ? tr("exact") : tr("aproximativ")}
                    </span>
                    <strong>{tr(r.denumire)}</strong>
                    <code>{r.cod}</code>
                    <span className="rezultat-context">
                      {tr(ETICHETA_CATEGORIE[r.categorie])}{r.context ? ` · ${r.context}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : (
        <>
          {poateAdministra && (
            <label className="comutator-dezactivate">
              <input
                type="checkbox"
                checked={aratăDezactivate}
                onChange={(e) => setAratăDezactivate(e.target.checked)}
              />
              {tr("arată și elementele dezactivate")}
            </label>
          )}

          <div className="taburi-sisteme">
            {arbore.map((s) => {
              const n = s.subsisteme.reduce((m, ss) => m + ss.tipuriComponenta.length, 0);
              return (
                <button
                  key={s.id}
                  className={"tab-sistem" + (sistem && s.id === sistem.id ? " activ" : "")}
                  onClick={() => setSistemActiv(s.id)}
                  title={tr("{0} — {1} tipuri", s.cod, n)}
                >
                  {tr(s.denumire)}
                  <span className="tab-numar">{n}</span>
                </button>
              );
            })}
          </div>

          {poateAdministra && propuneri.length > 0 && (
            <div className="panou panou-atentie">
              <div className="panou-antet"><span>{propuneri.length} {tr("propuneri din teren, în așteptare")}</span></div>
              <div className="panou-corp">
                <p className="subtitlu">
                  {tr("Tehnicienii le folosesc deja. Confirmă-le pe cele reale; pe cele care dublează un tip existent, respinge-le.")}
                </p>
                <table className="tabel">
                  <thead><tr><th>{tr("Denumire")}</th><th>{tr("Cod")}</th><th>{tr("Unde")}</th><th></th></tr></thead>
                  <tbody>
                    {propuneri.map((p) => (
                      <tr key={p.id}>
                        <td>{tr(p.denumire)}</td>
                        <td><code>{p.cod}</code></td>
                        <td>{tr(p.subsistem.tipSistem.denumire)} · {tr(p.subsistem.denumire)}</td>
                        <td className="celula-actiuni">
                          <button className="buton buton-mic" onClick={() => actiune(api.confirmaPropunere(p.id))}>{tr("confirmă")}</button>
                          <button className="buton buton-mic buton-sters" onClick={() => actiune(api.respingePropunere(p.id, "Respins la revizuire"))}>{tr("respinge")}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {sistem && sistem.subsisteme.map((sub) => {
            const deschis = !inchise[sub.id];
            return (
              <div className="panou" key={sub.id}>
                <div
                  className="panou-antet panou-antet-clic"
                  onClick={() => setInchise((d) => ({ ...d, [sub.id]: !!deschis }))}
                >
                  <span>
                    <span className="sageata">{deschis ? "▾" : "▸"}</span>
                    {tr(sub.denumire)}
                  </span>
                  <span className="numar">{sub.tipuriComponenta.length} {tr("tipuri")}</span>
                </div>

                {deschis && (
                  <div className="panou-corp">
                    <ul className="lista-tipuri">
                      {sub.tipuriComponenta.map((tip) => (
                        <li key={tip.id} className={tip.activ === false ? "tip-dezactivat" : ""}>
                          <span className={"pastila " + (tip.natura === "Software" ? "pastila-soft" : "pastila-hard")}>
                            {tip.natura === "Software" ? "soft" : "hard"}
                          </span>
                          <span className="tip-denumire">{tr(tip.denumire)}</span>
                          <code>{tip.cod}</code>
                          {tip.stare === "Propus" && <span className="eticheta">{tr("propus")}</span>}
                          {!tip.esteStandard && <span className="eticheta eticheta-client">{tr("adăugat de voi")}</span>}
                          {tip.activ === false && <span className="eticheta eticheta-inactiv">{tr("dezactivat")}</span>}
                          {tip.necesitaSerie && <span className="marcaj" title={tr("Se urmărește individual, cu serie")}>{tr("serie")}</span>}
                          {tip.durataViataLuni ? (
                            <span className="marcaj" title={tr("Durata de viață estimată")}>
                              {Math.round(tip.durataViataLuni / 12)} {tr("ani")}
                            </span>
                          ) : null}
                          {poateAdministra && (tip.activ === false ? (
                            <button className="buton buton-mic" onClick={() => actiune(api.reactiveazaTip(tip.id))}>
                              {tr("reactivează")}
                            </button>
                          ) : (
                            <button
                              className="buton buton-mic buton-sters"
                              onClick={() => actiune(api.dezactiveazaTip(tip.id))}
                              title={tr("Dispare din listele de selecție noi, dar rămâne pe fișele care îl conțin. Reversibil.")}
                            >
                              {tr("dezactivează")}
                            </button>
                          ))}
                        </li>
                      ))}
                    </ul>

                    {poatePropune && (
                      <button
                        className="buton buton-mic buton-adauga"
                        onClick={() => setFormular({ subsistemId: sub.id, unde: `${sistem.denumire} · ${sub.denumire}` })}
                      >
                        + {poateAdministra ? tr("adaugă echipament aici") : tr("propune echipament aici")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {formular && (
        <FormularTip
          formular={formular}
          poateAdministra={poateAdministra}
          laInchidere={() => setFormular(null)}
          laSalvare={async (date, propunere) => {
            await actiune(propunere ? api.propuneTip(date) : api.creeazaTip(date));
            setFormular(null);
          }}
        />
      )}
    </div>
  );
}

function FormularTip({ formular, poateAdministra, laInchidere, laSalvare }) {
  const [cod, setCod] = useState("");
  const [denumire, setDenumire] = useState("");
  const [natura, setNatura] = useState("Hardware");
  const [serie, setSerie] = useState(false);
  const lipsesc = [
    cod.trim().length < 2 && tr("un cod de cel puțin 2 caractere"),
    denumire.trim().length < 2 && tr("o denumire de cel puțin 2 caractere"),
  ].filter(Boolean);
  const gata = lipsesc.length === 0;

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{poateAdministra ? tr("Echipament nou în nomenclator") : tr("Propune un echipament")}</h3>
        <p className="subtitlu">{formular.unde}</p>

        <div className="formular-linie">
          <label>
            {tr("Denumire")}
            <input className="camp" value={denumire} onChange={(e) => setDenumire(e.target.value)} placeholder={tr("Cameră cu senzor LiDAR")} autoFocus />
          </label>
          <label>
            {tr("Cod")}
            <input className="camp" value={cod} onChange={(e) => setCod(e.target.value.toUpperCase())} placeholder="CAM-LIDAR" />
          </label>
        </div>

        <div className="formular-linie">
          <label>
            {tr("Natura")}
            <select className="camp" value={natura} onChange={(e) => setNatura(e.target.value)}>
              <option value="Hardware">{tr("Hardware — obiect fizic")}</option>
              <option value="Software">{tr("Software — licență sau platformă")}</option>
            </select>
          </label>
        </div>

        {natura === "Hardware" && (
          <label className="linie-bifa">
            <input type="checkbox" checked={serie} onChange={(e) => setSerie(e.target.checked)} />
            {tr("Se urmărește individual, cu serie de fabricație")}
          </label>
        )}

        <p className="nota-cautare">
          {poateAdministra
            ? tr("Rămâne înregistrat în nomenclator și apare de-acum în toate listele.")
            : tr("Îl poți folosi imediat; un manager tehnic îl confirmă mai târziu.")}
        </p>

        {lipsesc.length > 0 && (
          <p className="subtitlu">{tr("Completează:")} {lipsesc.join(", ")}.</p>
        )}

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("renunță")}</button>
          <button
            className="buton"
            disabled={!gata}
            onClick={() =>
              laSalvare(
                {
                  subsistemId: formular.subsistemId,
                  cod: cod.trim(),
                  denumire: denumire.trim(),
                  natura,
                  necesitaSerie: natura === "Hardware" ? serie : false,
                },
                !poateAdministra,
              )
            }
          >
            {poateAdministra ? tr("adaugă") : tr("propune")}
          </button>
        </div>
      </div>
    </div>
  );
}
